import type { TFunction } from 'i18next'
import { useState } from 'react'
import { Navigate, useParams } from 'react-router'
import { AmountCard, FlowLayout, Sheet, Skeleton } from '@/ds'
import { formatMoney } from '@/lib/format'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { isServiceError } from '@/services/errors'
import { pagosService, writePendingPayment } from '@/services/pagos'
import { reservaService } from '@/services/reserva'
import type { ChangeQuote, ChangeType, Stay } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { CardChoice, choiceErrors, chosenLast4, initialChoice, toCardInput } from '../Pagos/CardChoice'
import type { CardChoiceValue } from '../Pagos/CardChoice'
import { PAY_ROUTES, paymentErrorMessage } from '../Pagos/shared'
import { changeErrorMessage } from './Precio'
import { BOOKING_ROUTES, changeSummary, readDraft, TOTAL_CHANGE_STEPS, TYPE_BY_SLUG, useBookingStay, useLang, writeDraft, writeResult } from './shared'

const conceptKey = (type: ChangeType) => `v2.reserva.pagar.concepto.${type}`

function lead(quote: ChangeQuote, amount: string, differenceText: string, t: TFunction): string {
  // E11 `pendingPrice` incluye lo que ya quedaba pendiente de la reserva: el pago "Total" lo cobra todo.
  if (quote.pendingAfter && quote.pendingAfter.amount > quote.difference.amount) return t('v2.reserva.pagar.leadConPendiente', { amount, difference: differenceText })
  if (quote.changeFee?.amount === 0) return t('v2.reserva.pagar.leadGratis', { amount })
  if (quote.changeFee && quote.changeFee.amount > 0) return t('v2.reserva.pagar.leadConCoste', { amount })
  return t('v2.reserva.pagar.lead', { amount })
}

/**
 * 8.5 · Paso 3 de un cambio: pagar la diferencia (Reserva.dc.html screen=pay, §5d). Mismo orden y llamadas que la app
 * actual (D45): E12 cambia la reserva y después se cobra lo pendiente (E2 → E3 → banco → E4) en la pantalla de
 * autorización del bloque 3. Si el cobro no sale, "sin-pago" intenta volver a las fechas originales.
 */
export function CambiarPagar() {
  const { tipo = '' } = useParams()
  const type = TYPE_BY_SLUG[tipo]
  const stay = useBookingStay()
  const { search } = useAppNavigate()
  const [draft] = useState(() => (type ? readDraft(stay.guestCode, type) : null))
  if (!type) return <Navigate to={withQuery(BOOKING_ROUTES.detail, search)} replace />
  const quote = draft?.quote
  if (!quote || !quote.available || !quote.confirmable || quote.difference.amount <= 0) return <Navigate to={withQuery(draft?.input ? BOOKING_ROUTES.price(type) : BOOKING_ROUTES.pick(type), search)} replace />
  return <Pay stay={stay} type={type} quote={quote} />
}

function Pay({ stay, type, quote }: { stay: Stay; type: ChangeType; quote: ChangeQuote }) {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const { go } = useAppNavigate()
  const [summary, reload] = useAsync(`pagos:${stay.guestCode}:${apiLang}`, (signal) => pagosService.getPaymentSummary(stay, apiLang, signal))
  const s = summary.kind === 'ready' ? summary.value : null
  const [choice, setChoice] = useState<CardChoiceValue | null>(null)
  const current = choice ?? (s ? initialChoice(s) : null)
  const [showErrors, setShowErrors] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<{ text: string; expired: boolean } | null>(null)

  const charge = quote.pendingAfter && quote.pendingAfter.amount > 0 ? quote.pendingAfter : quote.difference
  const amount = formatMoney(charge, lang)
  const buttonLabel = t(type === 'dates' || type === 'nights' ? 'v2.reserva.pagar.botonFechas' : 'v2.reserva.pagar.boton', { amount })
  const last4 = s && current ? chosenLast4(s, current) : null

  const pay = async () => {
    if (!s || !current) return
    setStarting(true)
    setError(null)
    try {
      const res = await reservaService.confirmChange(stay, quote, toCardInput(s, current), apiLang)
      if (res.status === 'redirect') {
        writePendingPayment({ guestCode: stay.guestCode, kind: 'change', changeId: res.changeId, amount: res.amount.amount, currency: res.amount.currency, temporalPaidId: res.temporalPaidId, iframeUrl: res.iframeUrl, returnTo: BOOKING_ROUTES.done(type), retryTo: BOOKING_ROUTES.notCharged(type), startedAt: new Date().toISOString() })
        writeDraft(null)
        go(PAY_ROUTES.authorize, { state: { last4 } })
      } else if (res.status === 'payment_failed') {
        // Las fechas ya han cambiado y el cobro no ha empezado: mitigación de D45.
        writeDraft(null)
        go(BOOKING_ROUTES.notCharged(type), { replace: true, state: { changeId: res.changeId, cause: res.reason } })
      } else {
        writeResult(stay.guestCode, res.result)
        writeDraft(null)
        go(BOOKING_ROUTES.done(type), { replace: true })
      }
    } catch (e) {
      setConfirm(false)
      const expired = isServiceError(e) && e.code === 'quote_expired'
      setError({ text: isServiceError(e) && e.code === 'payment_failed' ? paymentErrorMessage(e, t) : changeErrorMessage(e, t), expired })
    } finally {
      setStarting(false)
    }
  }

  const notes = [t('v2.reserva.pagar.notaOrden'), quote.sendsReceipt ? t('v2.reserva.pagar.notaRecibo', { amount }) : null, quote.notifiesChannel && stay.channel.name ? t('v2.reserva.pagar.canal', { channel: stay.channel.name }) : null].filter(Boolean).join(' ')

  return (
    <FlowLayout
      progress={{ step: 3, total: TOTAL_CHANGE_STEPS, backLabel: t('v2.comun.atras'), onBack: () => go(BOOKING_ROUTES.price(type)), saved: false }}
      title={t('v2.reserva.pagar.titulo')}
      intro={lead(quote, amount, formatMoney(quote.difference, lang), t)}
      primary={
        s && current
          ? {
              label: buttonLabel,
              loading: starting,
              onClick: () => {
                setShowErrors(true)
                if (Object.keys(choiceErrors(s, current)).length === 0) setConfirm(true)
              },
            }
          : undefined
      }
      secondary={error?.expired ? { label: t('v2.reserva.pagar.volverPrecio'), onClick: () => go(BOOKING_ROUTES.price(type), { replace: true }) } : undefined}
      footerMessage={
        error ? (
          <p className="pagos__error" role="alert">
            {error.text}
          </p>
        ) : null
      }
    >
      <AmountCard total={amount} totalLabel={t(conceptKey(type), { change: changeSummary(quote, stay, lang, t) })} buttonLabel={null} />
      {summary.kind === 'loading' ? (
        <div aria-busy="true">
          <Skeleton height={176} />
        </div>
      ) : summary.kind === 'error' ? (
        <SectionError code={summary.code} onRetry={reload} />
      ) : s && current ? (
        <CardChoice summary={s} value={current} onChange={setChoice} showErrors={showErrors} disabled={starting} />
      ) : null}
      <p className="pagos__note">{notes}</p>
      <Sheet open={confirm} title={t('v2.pagos.pendiente.confirmarTitulo', { amount })} confirmLabel={buttonLabel} onConfirm={() => void pay()} onCancel={() => setConfirm(false)} loading={starting}>
        {`${t('v2.reserva.pagar.hojaOrden')} ${last4 ? t('v2.pagos.pendiente.confirmarGuardada', { last4 }) : t('v2.pagos.pendiente.confirmarNueva')}`}
      </Sheet>
    </FlowLayout>
  )
}
